import { useSnackbarComponent } from '../SnackbarComponents/useSnackbarComponent'
import {
  addNewBlogPostToSchoolRequest,
  addNewBlogSubscriberToSchoolRequest,
  deleteBlogPostRequest,
  getBlogSubscriberInSchoolRequest,
  getSchoolBlogPostDetailsRequest,
  getSchoolBlogPostsRequest,
  updateBlogPostRequest,
} from '../../services/api-requests/school-blog-request'

export type IPostEditorType = 'html' | 'md' | null

export interface ISchoolBlog {
  _id: any
  school_id: string
  title: string
  preview_post: string
  preview_image: string
  post?: string
  show_on_homepage: boolean
  archived: boolean
  published: boolean
  deleted: boolean
  slug?: string
  post_index?: number
  created_at?: string
  post_editor_type: IPostEditorType
}

export interface ISchoolSubscribers {
  _id: any
  school_id: string
  email: string
  subscribe_article_id?: string
  deleted: boolean
}

const useBlogs = () => {
  const snackbarHook = useSnackbarComponent()

  const addNewBlogPostToSchool = async (opts: {
    schoolId: string
    title: string
    previewPost: string
    previewImage: string
    post: string
    showOnHomepage: boolean
    published: boolean
    postEditorType: IPostEditorType
  }) => {
    try {
      const axiosRes = await addNewBlogPostToSchoolRequest(opts)
      snackbarHook.open('article added', 'success')
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error adding new blog post to school')
      snackbarHook.open('Error adding article', 'error')
    }
  }

  const updateBlogPost = async (opts: {
    postId: string
    schoolId: string
    title: string
    previewPost: string
    previewImage: string
    post: string
    showOnHomepage: boolean
    archived: boolean
    published: boolean
  }) => {
    try {
      const axiosRes = await updateBlogPostRequest(opts)
      snackbarHook.open('article updated', 'success')
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error updating new blog post to school')
      snackbarHook.open('Error updating article', 'error')
    }
  }

  const deleteBlogPost = async (opts: { postId: string; schoolId: string }) => {
    try {
      const axiosRes = await deleteBlogPostRequest(opts)
      snackbarHook.open('article deleted', 'success')
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error deleting article from school')
      snackbarHook.open('Error deleting article', 'error')
    }
  }

  const getSchoolBlogPosts = async (opts: {
    schoolId: string
    includeArchived: boolean
    includeUnpublished: boolean
    hideDetails: boolean
  }) => {
    try {
      const axiosRes = await getSchoolBlogPostsRequest(opts)
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error getting blog post from school')
      snackbarHook.open('Error getting articles', 'error')
    }
  }

  const getSchoolBlogPostDetails = async (opts: {
    postId: string
    schoolId: string
  }) => {
    try {
      const axiosRes = await getSchoolBlogPostDetailsRequest(opts)
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error getting blog post details from school')
      snackbarHook.open('Error getting article', 'error')
    }
  }

  const getSchoolBlogSubscribers = async (opts: { schoolId: string }) => {
    try {
      const axiosRes = await getBlogSubscriberInSchoolRequest(opts)
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error getting blog subscribers in school')
      snackbarHook.open('Error getting subscribers', 'error')
    }
  }

  const addSchoolBlogSubscriber = async (opts: {
    schoolId: string
    email: string
    subscribeArticleId?: string
  }) => {
    try {
      const axiosRes = await addNewBlogSubscriberToSchoolRequest(opts)
      snackbarHook.open('Subscribed successfully', 'success')
      return axiosRes?.data?.data
    } catch (e) {
      console.log('Error subscribing')
      snackbarHook.open('Error subscribing', 'error')
    }
  }

  return {
    addNewBlogPostToSchool,
    updateBlogPost,
    deleteBlogPost,
    getSchoolBlogPosts,
    getSchoolBlogPostDetails,
    getSchoolBlogSubscribers,
    addSchoolBlogSubscriber,
  }
}

export default useBlogs
